// Firebase Storage Utilities for Image Management
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { storage, IMAGE_CONSTRAINTS, analytics } from '../config/firebase';
import { logEvent } from 'firebase/analytics';

/**
 * Compress image before upload
 * @param {File} file - The image file to compress
 * @param {number} quality - Compression quality (0-1)
 * @returns {Promise<File>} Compressed image file
 */
export const compressImage = (file, quality = IMAGE_CONSTRAINTS.COMPRESSION_QUALITY) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas dimensions to image dimensions
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0);
        
        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = event.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Validate image file
 * @param {File} file - The image file to validate
 * @returns {Promise<{valid: boolean, message: string}>} Validation result
 */
export const validateImage = async (file) => {
  // Check file size
  if (file.size > IMAGE_CONSTRAINTS.MAX_FILE_SIZE) {
    return {
      valid: false,
      message: `File size exceeds ${IMAGE_CONSTRAINTS.MAX_FILE_SIZE / (1024 * 1024)}MB limit`
    };
  }

  // Check file type
  if (!IMAGE_CONSTRAINTS.ALLOWED_FORMATS.includes(file.type)) {
    return {
      valid: false,
      message: 'Invalid file format. Allowed: JPEG, PNG, WebP, GIF'
    };
  }

  // Check image dimensions
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        if (img.width < IMAGE_CONSTRAINTS.MIN_RESOLUTION || img.height < IMAGE_CONSTRAINTS.MIN_RESOLUTION) {
          resolve({
            valid: true,
            message: `Warning: Image resolution is ${img.width}x${img.height}. Recommended minimum: ${IMAGE_CONSTRAINTS.MIN_RESOLUTION}x${IMAGE_CONSTRAINTS.MIN_RESOLUTION}`,
            warning: true,
            resolution: `${img.width}x${img.height}`
          });
        } else {
          resolve({
            valid: true,
            message: `Image approved (${img.width}x${img.height})`,
            resolution: `${img.width}x${img.height}`
          });
        }
      };
      img.onerror = () => {
        resolve({ valid: false, message: 'Failed to load image' });
      };
      img.src = event.target.result;
    };
    reader.onerror = () => {
      resolve({ valid: false, message: 'Failed to read file' });
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Initialize folder structure in Firebase Storage
 * Firebase Storage auto-creates folders on first file upload, but this helper
 * ensures consistent folder structure
 * @param {string} folderName - Folder to initialize (e.g., 'products', 'cultures', 'users', 'vendors')
 * @returns {Promise<void>}
 */
export const initializeFolder = async (folderName) => {
  try {
    // Create a placeholder/marker file to ensure folder exists
    const markerPath = `${folderName}/.marker`;
    const markerRef = ref(storage, markerPath);
    const markerContent = new Blob(['.'], { type: 'text/plain' });
    
    // Check if marker already exists to avoid unnecessary uploads
    try {
      const markerUrl = await getDownloadURL(markerRef);
      // Marker exists, folder is initialized
      return;
    } catch (error) {
      // Marker doesn't exist, create it
      await uploadBytes(markerRef, markerContent);
    }
  } catch (error) {
    console.warn(`Could not initialize folder ${folderName}:`, error);
    // Don't throw - folder will be created on first file upload
  }
};

/**
 * Initialize all standard folder structure
 * @returns {Promise<void>}
 */
export const initializeFolders = async () => {
  const folders = ['products', 'cultures', 'users', 'vendors', 'temp'];
  
  try {
    await Promise.all(folders.map(folder => initializeFolder(folder)));
    console.log('✓ Firebase Storage folders initialized');
  } catch (error) {
    console.error('Error initializing folders:', error);
  }
};

/**
 * Get proper storage path based on collection type
 * @param {string} collection - Collection type (products, cultures, users, vendors)
 * @param {string} itemId - Item ID
 * @param {string} type - Sub-type (images, thumbnails, logos, etc.)
 * @returns {string} Formatted storage path
 */
export const getStoragePath = (collection, itemId = '', type = 'images') => {
  const parts = [collection];
  
  if (itemId) {
    parts.push(itemId);
  }
  
  if (type && type !== 'images') {
    parts.push(type);
  }
  
  return parts.join('/') + '/';
};

/**
 * Upload image to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} path - Storage path (e.g., 'products/product-id/')
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<string>} Download URL of uploaded image
 */
export const uploadImage = async (file, path, onProgress) => {
  try {
    // Validate image
    const validation = await validateImage(file);
    if (!validation.valid) {
      throw new Error(validation.message);
    }

    // Extract folder from path and initialize it
    const folderMatch = path.match(/^([^/]+)/);
    if (folderMatch) {
      const folder = folderMatch[1];
      await initializeFolder(folder).catch(() => {
        // Silently fail - folder will be created on upload
      });
    }

    // Compress image for faster upload and optimal storage
    const compressedFile = await compressImage(file);

    // Create unique filename with timestamp
    const timestamp = Date.now();
    const filename = `${timestamp}-${file.name.replace(/\s+/g, '-')}`;
    const storagePath = `${path}${filename}`;

    // Create storage reference
    const fileRef = ref(storage, storagePath);

    // Upload file
    const snapshot = await uploadBytes(fileRef, compressedFile, {
      cacheControl: 'public, max-age=31536000' // Cache for 1 year
    });

    // Get download URL
    const downloadUrl = await getDownloadURL(snapshot.ref);

    // Log analytics event
    logEvent(analytics, 'image_uploaded', {
      path: path,
      filename: filename,
      size: compressedFile.size,
      timestamp: new Date().toISOString()
    });

    return downloadUrl;
  } catch (error) {
    console.error('Image upload error:', error);
    logEvent(analytics, 'image_upload_failed', {
      error: error.message,
      path: path
    });
    throw error;
  }
};

/**
 * Upload multiple images to Firebase Storage
 * @param {File[]} files - Array of image files to upload
 * @param {string} path - Storage path
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<string[]>} Array of download URLs
 */
export const uploadMultipleImages = async (files, path, onProgress) => {
  const urls = [];
  const totalFiles = files.length;

  for (let i = 0; i < totalFiles; i++) {
    try {
      const url = await uploadImage(files[i], path);
      urls.push(url);

      // Call progress callback
      if (onProgress) {
        onProgress({
          current: i + 1,
          total: totalFiles,
          percentage: Math.round(((i + 1) / totalFiles) * 100),
          currentFile: files[i].name
        });
      }
    } catch (error) {
      console.error(`Failed to upload ${files[i].name}:`, error);
      // Continue with next file
    }
  }

  if (urls.length === 0) {
    throw new Error('Failed to upload any images');
  }

  return urls;
};

/**
 * Delete image from Firebase Storage
 * @param {string} imageUrl - Download URL of image to delete
 * @returns {Promise<void>}
 */
export const deleteImage = async (imageUrl) => {
  try {
    // Extract path from URL
    const decodedUrl = decodeURIComponent(imageUrl);
    const pathMatch = decodedUrl.match(/\/o\/(.+?)\?alt/);
    
    if (!pathMatch) {
      throw new Error('Invalid image URL');
    }

    const filePath = pathMatch[1];
    const fileRef = ref(storage, filePath);

    // Delete file
    await deleteObject(fileRef);

    // Log analytics event
    logEvent(analytics, 'image_deleted', {
      path: filePath,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Image deletion error:', error);
    throw error;
  }
};

/**
 * Generate image URL with optimization parameters
 * @param {string} imageUrl - Original image URL
 * @param {Object} options - Optimization options
 * @returns {string} Optimized image URL
 */
export const getOptimizedImageUrl = (imageUrl, options = {}) => {
  const {
    width = 800,
    height = 800,
    quality = 85,
    format = 'webp'
  } = options;

  // Firebase Storage doesn't have built-in image optimization
  // This can be implemented with Cloud Functions or Imgix integration
  // For now, return original URL
  return imageUrl;
};

/**
 * Get list of images in a directory
 * @param {string} path - Storage path
 * @returns {Promise<string[]>} Array of image URLs
 */
export const listImages = async (path) => {
  try {
    const listRef = ref(storage, path);
    const result = await listAll(listRef);

    const urls = await Promise.all(
      result.items.map(item => getDownloadURL(item))
    );

    return urls;
  } catch (error) {
    console.error('List images error:', error);
    throw error;
  }
};

/**
 * Track image view analytics
 * @param {string} imagePath - Path to image
 * @param {Object} metadata - Additional metadata
 */
export const trackImageView = (imagePath, metadata = {}) => {
  logEvent(analytics, 'image_viewed', {
    path: imagePath,
    timestamp: new Date().toISOString(),
    ...metadata
  });
};

/**
 * Track image download analytics
 * @param {string} imagePath - Path to image
 * @param {Object} metadata - Additional metadata
 */
export const trackImageDownload = (imagePath, metadata = {}) => {
  logEvent(analytics, 'image_downloaded', {
    path: imagePath,
    timestamp: new Date().toISOString(),
    ...metadata
  });
};

export default {
  initializeFolder,
  initializeFolders,
  getStoragePath,
  compressImage,
  validateImage,
  uploadImage,
  uploadMultipleImages,
  deleteImage,
  getOptimizedImageUrl,
  listImages,
  trackImageView,
  trackImageDownload
};
